import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import Select from 'react-select';
import {
  updateUser, updateOrganization, updateUserAddresses, updateOrgAddresses,
  selectUserInfo, setProfileUpdated
} from '../redux/userSlice';
import { selectTokens } from '../redux/userSlice';
import US_STATES from '../constants/states';
import LoadingAnimation from './LoadingAnimation';
import { downloadTaxSaleBidderRegistrationCard } from '../redux/registrationSlice';
import { Container, Row, Col, Button, Toast, ToastContainer } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faDownload } from '@fortawesome/free-solid-svg-icons';
import { useDocumentTitle } from "../hooks/useDocumentTitle";

const emptyAddress = { street_address: '', street_address2: '', city: '', state: '', zip: '' };
const emptyOrg = {
  org_id: null,
  business_name: '',
  first_name: '',
  middle_name: '',
  last_name: '',
  phone: '',
  fax: '',
  email: '',
  addresses: [emptyAddress]
};

const SERVER_API_KEY = process.env.REACT_APP_SERVER_API_KEY;

// Only compare keys you actually persist
const USER_KEYS = ["first_name","middle_name","last_name","phone","email","username","county_id","global_reg_number"];
const ORG_KEYS  = ["org_id","business_name","first_name","middle_name","last_name","phone","fax"];
const ADDR_KEYS = ["street_address","street_address2","city","state","zip"];

// Simple deep equality for plain data
const deepEqual = (a, b) => {
  if (a === b) return true;
  try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
};

// Return only changed fields (preserves clears like ""/null when they ARE the change)
const diffPatch = (next = {}, prev = {}, keys) => {
  const all = keys ?? Array.from(new Set([...(Object.keys(next||{})), ...(Object.keys(prev||{}))]));
  const patch = {};
  for (const k of all) {
    if (!deepEqual(next?.[k], prev?.[k])) patch[k] = next?.[k];
  }
  return patch;
};

const firstAddr = (o) => o?.addresses?.[0] ?? null;

const Profile = () => {
  useDocumentTitle("Profile");
  const { user = {}, organizations = [] } = useSelector(selectUserInfo) || {};
  const tokens = useSelector(selectTokens);
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [localUserInfo, setLocalUserInfo] = useState(user);
  const [localOrgInfo, setLocalOrgInfo] = useState(organizations[0] || null); // keep null when no org linked
  const [, setLocalNotifications] = useState(user.notifications || []);
  const [, setShowSuccessModal] = useState(false);
  const [sendEmailPreference, setSendEmailPreference] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);

  // initial snapshots for change highlighting
  const [initialUserInfo, setInitialUserInfo] = useState({});
  const [initialOrgInfo, setInitialOrgInfo] = useState(null);

  const stateOptions = US_STATES.map(state => ({ value: state.code, label: state.name }));

  const renderDropdown = (name, label, value, onChangeHandler, idPrefix = '') => {
    const inputId = `${idPrefix}${name}`;
    return (
      <div className="col-md-4">
        <div className="label-bg">
          <label htmlFor={inputId}>{label}:</label>
        </div>
        <Select
          inputId={inputId}
          aria-label={label}
          className="form-control"
          value={stateOptions.find(option => option.value === value)}
          onChange={(option) => onChangeHandler(name, option.value)}
          options={stateOptions}
          placeholder="Select a State"
          styles={{ placeholder: (base) => ({ ...base, color: '#595959' }) }}
        />
      </div>
    );
  };

  useEffect(() => {
    const updatedUser = {
      ...user,
      addresses: user?.addresses?.length > 0 ? user.addresses : [emptyAddress]
    };
    const updatedOrg = organizations?.length > 0
      ? { ...organizations[0], addresses: organizations[0]?.addresses?.length > 0 ? organizations[0].addresses : [emptyAddress] }
      : null; // keep null when none

    setLocalUserInfo(updatedUser);
    setLocalOrgInfo(updatedOrg);
    setLocalNotifications(user?.notifications || []);

    setInitialUserInfo(updatedUser);
    setInitialOrgInfo(updatedOrg);
  }, [user, organizations]);

  useEffect(() => {
    if (user?.fusionauth_user_id && tokens.accessToken) {
      fetchUserPreferences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.fusionauth_user_id, tokens.accessToken]);

  const fetchUserPreferences = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/preferences/user/${user.fusionauth_user_id}`,
        { headers: { "x-api-key": SERVER_API_KEY, Authorization: `Bearer ${tokens.accessToken}` } }
      );
      if (response.status === 200) {
        const preferences = response.data;
        setSendEmailPreference(preferences.sendEmail === 1);
      }
    } catch (error) {
      console.error('Error fetching user preferences:', error);
    }
  };

  const handleDownloadRegistrationCardPDF = async () => {
    setDownloadLoading(true);
    try {
      await dispatch(downloadTaxSaleBidderRegistrationCard({ selectedUser: user })).unwrap();
    } catch (error) {
      console.error("Error retrieving tax sale bidder registration document:", error);
      alert("Failed to retrieve Tax Sale Bidder Registration Document.");
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleUserInfoChange = (field, value) => {
    setLocalUserInfo({ ...localUserInfo, [field]: value });
  };

  const handleOrgInfoChange = (field, value) => {
    const next = (localOrgInfo ?? { ...emptyOrg }); // start an org if user types in org section
    setLocalOrgInfo({ ...next, [field]: value });
  };

  const handleRevertChanges = () => {
    setLocalUserInfo(initialUserInfo);
    setLocalOrgInfo(initialOrgInfo);
    setLocalNotifications(initialUserInfo.notifications || []);
  };

  const handleUserAddressChange = (field, value) => {
    const updatedAddress = localUserInfo.addresses?.[0]
      ? { ...localUserInfo.addresses[0], [field]: value }
      : { ...emptyAddress, [field]: value };
    setLocalUserInfo({ ...localUserInfo, addresses: [updatedAddress] });
  };

  const handleOrgAddressChange = (field, value) => {
    const base = (localOrgInfo ?? { ...emptyOrg });
    const updatedAddress = base.addresses?.[0]
      ? { ...base.addresses[0], [field]: value }
      : { ...emptyAddress, [field]: value };
    setLocalOrgInfo({ ...base, addresses: [updatedAddress] });
  };

  const isValueChanged = (name, value) => initialUserInfo[name] !== value;

  const renderInputField = (name, label, value, onChangeHandler, idPrefix = '', isEditable = true) => {
    const inputId = `${idPrefix}${name}`;
    return (
      <div className="col-md-4">
        <div className="label-bg">
          <label htmlFor={inputId}>{label}:</label>
        </div>
        <input
          id={inputId}
          className={`form-control ${isValueChanged(name, value) ? 'glow-yellow' : ''}`}
          type="text"
          value={value || ''}
          onChange={(e) => onChangeHandler(name, e.target.value)}
          disabled={!isEditable}
        />
      </div>
    );
  };

  const handleToggleSendEmail = async (e) => {
    const newValue = e.target.checked;
    setSendEmailPreference(newValue);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/preferences/user/${user.fusionauth_user_id}/update`,
        {
          setting_name: "sendEmail",
          setting_value: newValue ? 1 : 0,
          setting_description: "Receive New Sale Notifications via Email",
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-api-key": SERVER_API_KEY,
            Authorization: `Bearer ${tokens.accessToken}`,
          },
        }
      );
      if (response.status !== 200) throw new Error('Failed to update preferences');
    } catch (error) {
      console.error('Error updating preferences:', error);
      setSendEmailPreference(!newValue);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      // NEXT (form)
      const userNext = { ...localUserInfo };
      const uaNext   = firstAddr(localUserInfo) || {};

      const orgNext  = localOrgInfo ? { ...localOrgInfo } : null;
      const oaNext   = orgNext ? (firstAddr(orgNext) || {}) : null;

      // PREV (store)
      const userPrev = user || {};
      const uaPrev   = firstAddr(user) || {};

      const orgPrev  = organizations?.[0] || null;
      const oaPrev   = orgPrev ? (firstAddr(orgPrev) || {}) : null;

      // patches (only changed fields)
      const userPatch = diffPatch(userNext, userPrev, USER_KEYS);
      const uaPatch   = diffPatch(uaNext,   uaPrev,   ADDR_KEYS);

      let orgPatch = {};
      let oaPatch  = {};
      const hadOrg = !!orgPrev;
      const hasOrg = !!orgNext;

      if (hadOrg && hasOrg) {
        orgPatch = diffPatch(orgNext, orgPrev, ORG_KEYS);
        oaPatch  = diffPatch(oaNext || {}, oaPrev || {}, ADDR_KEYS);
      } else if (!hadOrg && hasOrg) {
        // brand new org
        orgPatch = diffPatch(orgNext, {}, ORG_KEYS);
        oaPatch  = diffPatch(oaNext || {}, {}, ADDR_KEYS);
      } else if (hadOrg && !hasOrg) {
        // user removed org: send delete contract (adjust if your API prefers empties)
        orgPatch = { org_id: orgPrev.org_id, _delete: true };
        if (Object.keys(oaPrev || {}).length) {
          oaPatch = { org_id: orgPrev.org_id, _delete: true };
        }
      }

      // Build payload from non-empty patches
      const payload = {};

      if (Object.keys(userPatch).length) {
        const { addresses, applications, notifications, roles, ...safe } = userPatch;
        payload.userData = safe;
      }
      if (Object.keys(uaPatch).length) {
        payload.userAddress = { ...uaPrev, ...uaPatch };
      }
      if (Object.keys(orgPatch).length) {
        const { addresses, ...safeOrg } = orgPatch;
        payload.orgData = safeOrg;
      }
      if (Object.keys(oaPatch).length) {
        const org_id = oaNext?.org_id ?? orgNext?.org_id ?? orgPrev?.org_id ?? organizations?.[0]?.org_id ?? null;
        payload.orgAddress = { ...(oaPrev || {}), ...oaPatch, ...(org_id ? { org_id } : {}) };
      }

      if (Object.keys(payload).length === 0) {
        setToastMessage("No changes detected.");
        setShowToast(true);
        return;
      }

      const res = await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/app/me/userinfo`,
        payload,
        { headers: { Authorization: `Bearer ${tokens.accessToken}`, 'x-api-key': SERVER_API_KEY, 'Content-Type': 'application/json' } }
      );

      // use server’s canonical response to update Redux (prevents drift)
      const { user: uResp, organizations: orgResp } = res.data || {};
      if (uResp) {
        dispatch(updateUser(uResp));
        dispatch(updateUserAddresses(uResp.addresses || []));
      }
      if (Array.isArray(orgResp)) {
        dispatch(updateOrganization(orgResp));
        const first = orgResp[0];
        dispatch(updateOrgAddresses(first?.addresses || []));
      }

      dispatch(setProfileUpdated(true));
      setShowSuccessModal(true);
      setToastMessage("Profile updated successfully");
      setShowToast(true);
    } catch (error) {
      console.error('Error in updating profile', error);
      setToastMessage("Error in updating profile.");
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return <LoadingAnimation />;

  const orgView = localOrgInfo ?? { ...emptyOrg }; // for rendering only

  return (
    <div className="container mt-4">
      <h1 className="visually-hidden">Profile</h1>
      <ToastContainer position="top-end" className="p-3 unique-auction-toast-container" aria-live="polite">
        <Toast onClose={() => setShowToast(false)} show={showToast} delay={8000} autohide bg="warning">
          <Toast.Header>
            <strong className="me-auto">Profile</strong>
          </Toast.Header>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
      <div className="accordion" id="profileAccordion">
        {/* User Information Section */}
        <div className="accordion-item">
          <h2 className="accordion-header" id="userInformationHeader">
            <button
              className="accordion-button"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#userInformationCollapse"
              aria-expanded="true"
            >
              User Information
            </button>
          </h2>
          <div id="userInformationCollapse" className="accordion-collapse collapse show">
            <div className="accordion-body">
              <div className="row">
                {renderInputField("first_name", "First Name", localUserInfo.first_name, handleUserInfoChange, "user-")}
                {renderInputField("middle_name", "Middle Name", localUserInfo.middle_name, handleUserInfoChange, "user-")}
                {renderInputField("last_name", "Last Name", localUserInfo.last_name, handleUserInfoChange, "user-")}
                {renderInputField("phone", "Phone Number", localUserInfo.phone, handleUserInfoChange, "user-")}
                {renderInputField("email", "Email", localUserInfo.email, handleUserInfoChange, "user-", false)}
              </div>
              <div className="row">
                {renderInputField(
                  "street_address",
                  "Street Address",
                  localUserInfo.addresses?.[0]?.street_address,
                  handleUserAddressChange,
                  "user-"
                )}
                {renderInputField(
                  "street_address2",
                  "Street Address 2",
                  localUserInfo.addresses?.[0]?.street_address2,
                  handleUserAddressChange,
                  "user-"
                )}
                {renderInputField("city", "City", localUserInfo.addresses?.[0]?.city, handleUserAddressChange, "user-")}
                {renderDropdown("state", "State", localUserInfo.addresses?.[0]?.state, handleUserAddressChange, "user-")}
                {renderInputField("zip", "Zip Code", localUserInfo.addresses?.[0]?.zip, handleUserAddressChange, "user-")}
              </div>
            </div>
          </div>
        </div>

        {/* Organization Information Section */}
        <div className="accordion-item">
          <h2 className="accordion-header" id="organizationHeader">
            <button
              className="accordion-button collapsed"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#organizationCollapse"
              aria-expanded="false"
              aria-controls="organizationCollapse"
            >
              Organization Information
            </button>
          </h2>
          <div id="organizationCollapse" className="accordion-collapse collapse">
            <div className="accordion-body">
              <div className="row">
                {renderInputField("business_name", "Does Business As", orgView.business_name, handleOrgInfoChange, "org-")}
                {renderInputField("first_name", "Contact First Name", orgView.first_name, handleOrgInfoChange, "org-")}
                {renderInputField("middle_name", "Contact Middle Name", orgView.middle_name, handleOrgInfoChange, "org-")}
                {renderInputField("last_name", "Contact Last Name", orgView.last_name, handleOrgInfoChange, "org-")}
                {renderInputField("phone", "Phone Number", orgView.phone, handleOrgInfoChange, "org-")}
                {renderInputField("fax", "Fax Number", orgView.fax, handleOrgInfoChange, "org-")}
              </div>
              <div className="row">
                {renderInputField(
                  "street_address",
                  "Street Address",
                  orgView.addresses?.[0]?.street_address,
                  handleOrgAddressChange,
                  "org-"
                )}
                {renderInputField(
                  "street_address2",
                  "Street Address 2",
                  orgView.addresses?.[0]?.street_address2,
                  handleOrgAddressChange,
                  "org-"
                )}
                {renderInputField("city", "City", orgView.addresses?.[0]?.city, handleOrgAddressChange, "org-")}
                {renderDropdown("state", "State", orgView.addresses?.[0]?.state, handleOrgAddressChange, "org-")}
                {renderInputField("zip", "Zip Code", orgView.addresses?.[0]?.zip, handleOrgAddressChange, "org-")}
              </div>
            </div>
          </div>
        </div>

        {/* Documents Section */}
        <div className="accordion-item">
          <h2 className="accordion-header" id="documentsHeader">
            <button
              className="accordion-button collapsed"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#documentsCollapse"
            >
              Documents
            </button>
          </h2>
          <div id="documentsCollapse" className="accordion-collapse collapse">
            <div className="accordion-body">
              <Container className="shadow-none p-0 m-2">
                <Row className="align-items-center">
                  <Col sm={8}>Tax Bidder Registration Document</Col>
                  <Col sm={4} className="justify-content-end d-flex">
                    <Button
                      variant="primary"
                      disabled={downloadLoading}
                      className="d-flex align-items-center justify-content-evenly me-2"
                      aria-label="Download Tax Bidder Registration Document"
                      onClick={() => handleDownloadRegistrationCardPDF()}
                    >
                      <>
                        {!downloadLoading ? (
                          <FontAwesomeIcon icon={faDownload} />
                        ) : (
                          <FontAwesomeIcon icon={faSpinner} spin />
                        )}
                      </>
                      <span className="d-none d-md-inline ps-2">Download</span>
                    </Button>
                  </Col>
                </Row>
              </Container>
            </div>
          </div>
        </div>

        {/* Notifications Section */}
        <div className="mt-4">
          <center>
            <h3>Notifications Preferences</h3>
            <div
              className="form-group"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                marginTop: "10px",
                marginBottom: "20px",
                padding: "20px",
                border: "1px solid #d3d3d3",
                borderRadius: "8px",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                maxWidth: "400px",
                margin: "0 auto",
                backgroundColor: sendEmailPreference ? "#d4f7dc" : "#ffd6d6", // Green for ON, Red for OFF
              }}
            >
              <label htmlFor="send-email-switch" style={{ marginBottom: "10px", fontSize: "16px", fontWeight: "bold" }}>
                Receive New Sale Notifications via Email
              </label>
              <input
                type="checkbox"
                id="send-email-switch"
                checked={sendEmailPreference}
                onChange={handleToggleSendEmail}
                style={{ transform: "scale(1.5)", marginBottom: "10px" }}
              />
              <div style={{ fontSize: "14px", fontWeight: "bold", color: sendEmailPreference ? "darkgreen" : "darkred" }}>
                {sendEmailPreference ? "Notifications Enabled" : "Notifications Disabled"}
              </div>
            </div>
          </center>
        </div>

        {/* Save Changes Button */}
        <div className="mt-4">
          <center>
            <button className="btn btn-primary" onClick={handleSubmit}>
              Save Changes
            </button>
            <button className="btn btn-secondary" onClick={handleRevertChanges} disabled={isLoading}>
              Revert Changes
            </button>
          </center>
        </div>
      </div>
    </div>
  );
};

export default Profile;
